// Known-vulnerability (CVE) summary of an endpoint device. The API (cma/endpointcve and cma/softwaredrift) looks the
// CVEs up from the programs the installed-programs script (Get-InstalledPrograms) reported, and answers with a
// "cve" section; this module turns that section into the status shown on the Endpoint Devices page.
// Plain functions only (no React), so they can be unit tested.

// The schedule row name of the installed-programs script (21 characters: under the 32 character row-name cut).
export const CVE_SCRIPT = 'get-installedprograms';
export const isCveScript = (name) => String(name || '').toLowerCase() === CVE_SCRIPT;

// The fields the API (cma/endpointcve) sends for every device: enough for the bubbles and the per-status device list.
// The whole result is only loaded when one device is opened. The API's FLEET_FIELDS (cma-api,
// routes/cma/endpointcve/_fleet.py) is the same list: keep the two the same.
export const CVE_FIELDS = [
  'computerName',
  'os.name',
  'cve.status',
  'cve.message',
  'cve.uniqueCves',
  // The whole machine (the only numbers an older result has) ...
  'cve.uniqueCvesBySeverity.CRITICAL',
  'cve.uniqueCvesBySeverity.HIGH',
  'cve.uniqueCvesBySeverity.MEDIUM',
  'cve.uniqueCvesBySeverity.LOW',
  // ... the software alone, which drives the status ...
  'cve.software.uniqueCves',
  'cve.software.uniqueCvesBySeverity.CRITICAL',
  'cve.software.uniqueCvesBySeverity.HIGH',
  'cve.software.uniqueCvesBySeverity.MEDIUM',
  'cve.software.uniqueCvesBySeverity.LOW',
  // ... and Windows alone, shown on its own line.
  'cve.os.uniqueCves',
  'cve.counts.vulnerable',
  'cve.counts.cannotAssess',
  'cve.counts.lowConfidenceOnly',
  'cve.unusedWithCves',
  'cve.topSoftware',
];

export const CVE_STATUS = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM_LOW: 'Medium or low',
  UNASSESSED: 'None found, some not assessed',
  CLEAN: 'No known CVEs',
  FAILED: 'CVE lookup failed',
  NO_DATA: 'No CVE data',
};

// The order of the bubbles: worst first.
export const CVE_STATUS_ORDER = [
  CVE_STATUS.CRITICAL, CVE_STATUS.HIGH, CVE_STATUS.MEDIUM_LOW, CVE_STATUS.UNASSESSED, CVE_STATUS.CLEAN,
  CVE_STATUS.FAILED, CVE_STATUS.NO_DATA,
];

// MUI chip colour per status; anything else stays neutral.
export const CVE_STATUS_COLORS = {
  [CVE_STATUS.CRITICAL]: 'error',
  [CVE_STATUS.HIGH]: 'warning',
  [CVE_STATUS.MEDIUM_LOW]: 'info',
  [CVE_STATUS.UNASSESSED]: 'default',
  [CVE_STATUS.CLEAN]: 'success',
  [CVE_STATUS.FAILED]: 'error',
  [CVE_STATUS.NO_DATA]: 'default',
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// One device's CVE numbers from its selected fields (VES returns each path under its dotted name). The severity
// numbers are the software's: the Windows operating system alone is about a thousand CVEs on a machine that is a
// little behind on updates, and would drown them. A result from before the split has no software numbers, and its
// whole-machine numbers are used instead.
export const cveNumbers = (item) => {
  const r = item?.results || {};
  const split = r['cve.software.uniqueCves'] !== undefined && r['cve.software.uniqueCves'] !== null;
  const sev = (name) => num(split ? r[`cve.software.uniqueCvesBySeverity.${name}`] : r[`cve.uniqueCvesBySeverity.${name}`]);
  return {
    critical: sev('CRITICAL'),
    high: sev('HIGH'),
    medium: sev('MEDIUM'),
    low: sev('LOW'),
    unique: num(split ? r['cve.software.uniqueCves'] : r['cve.uniqueCves']),
    // The Windows operating system's own CVEs: fixed by installing Windows updates, so kept out of the rest.
    windows: split ? num(r['cve.os.uniqueCves']) : 0,
    programsWithCves: num(r['cve.counts.vulnerable']),
    cannotAssess: num(r['cve.counts.cannotAssess']),
    notInDataset: num(r['cve.counts.notInDataset']),
    assessedClean: num(r['cve.counts.assessedClean']),
    lowConfidenceOnly: num(r['cve.counts.lowConfidenceOnly']),
    // Programs with CVEs that have not been used for a long time (a remove-or-update decision).
    unusedWithCves: Array.isArray(r['cve.unusedWithCves']) ? r['cve.unusedWithCves'].length : num(r['cve.unusedWithCves']),
  };
};

// "No known CVEs" is only shown when nothing was left unjudged. Programs that have CVE records but whose version
// ranges cannot be compared (cannot assess), or whose only matches are low confidence, were not cleared, so a
// machine with such programs is not shown as clean. Programs no CVE record mentions at all are not counted: for
// most agents and drivers that is simply the normal case.
export const unassessedCount = (item) => {
  const n = cveNumbers(item);
  return n.cannotAssess + n.lowConfidenceOnly;
};

// A device's overall CVE status. A device whose CVEs could not be looked up (or that has not reported)
// has no "cve" section at all, and is shown as "No CVE data" instead of being counted as clean.
export const cveStatusOf = (item) => {
  const status = item?.results?.['cve.status'];
  if (status === 'error') return CVE_STATUS.FAILED;
  if (status !== 'ok') return CVE_STATUS.NO_DATA;
  const n = cveNumbers(item);
  if (n.critical > 0) return CVE_STATUS.CRITICAL;
  if (n.high > 0) return CVE_STATUS.HIGH;
  if (n.medium > 0 || n.low > 0 || n.unique > 0) return CVE_STATUS.MEDIUM_LOW;
  return unassessedCount(item) > 0 ? CVE_STATUS.UNASSESSED : CVE_STATUS.CLEAN;
};

// [{ status, count }] for the bubbles: worst first, statuses nobody has are left out.
export const cveStatusCounts = (items) => {
  const counts = {};
  (items || []).forEach(item => {
    const status = cveStatusOf(item);
    counts[status] = (counts[status] || 0) + 1;
  });
  return CVE_STATUS_ORDER.filter(s => counts[s]).map(status => ({ status, count: counts[status] }));
};

// Chip colour of one CVE's severity.
export const severityColor = (severity) => {
  switch (String(severity || '').toUpperCase()) {
    case 'CRITICAL': return 'error';
    case 'HIGH': return 'warning';
    case 'MEDIUM': return 'info';
    default: return 'default';
  }
};

// "2 critical, 21 high, 38 medium" from a { CRITICAL: 2, HIGH: 21, ... } object; zeros are left out.
export const severityMix = (bySeverity) => {
  const s = bySeverity || {};
  return [['CRITICAL', 'critical'], ['HIGH', 'high'], ['MEDIUM', 'medium'], ['LOW', 'low'], ['NONE', 'unscored']]
    .filter(([key]) => num(s[key]) > 0)
    .map(([key, label]) => `${num(s[key])} ${label}`)
    .join(', ');
};

// The National Vulnerability Database page of a CVE id; null for anything that is not a plain CVE id, so a
// result can never put an arbitrary link on the page.
export const nvdUrl = (cve) => (/^CVE-\d{4}-\d{4,}$/i.test(String(cve || ''))
  ? `https://nvd.nist.gov/vuln/detail/${String(cve).toUpperCase()}`
  : null);
